import type { ButtonTheme } from "@ui/Button";
import { toast } from "@ui/molecules/Toasts/toast";
import { getErrorMessage } from "@utils/getErrorMessage";
import { getPreferredLocale } from "@utils/locale";
import {
	batch,
	createEffect,
	createSignal,
	on,
	onCleanup,
	onMount,
} from "solid-js";
import { effect } from "solid-js/web";
import {
	createQuestion,
	getVideoSampledFrames,
	type SearchInfluencersData,
	type SearchVideosData,
	searchInfluencers,
	searchPublicAskPageVideos,
	searchSocialChannels,
	searchVideos,
	type VideoSearchResult,
} from "@/client";
import type { AccountCompletionItem } from "./accountCompletion";
import { resolveAccountMentionAvatars } from "./accountMentionAvatars";
import {
	augmentedTextFromClipboardHtml,
	clipboardHtmlForBlocks,
} from "./promptClipboardHtml";
import { promptWithoutVideoThumbnails } from "./promptMentionSerialization";
import {
	createQuestionBoxAccountMention,
	showAccountMentionAvatars,
} from "./QuestionBoxAccountMention";
import { ATOM_SELECTED_CLASS } from "./QuestionBoxAtom";
import {
	createQuestionBoxVideoMention,
	type QuestionBoxVideoMentionData,
	questionBoxVideoMentionKey,
} from "./QuestionBoxVideoMention";
import type { QuestionContextAnswer } from "./QuestionContext";
import type { QuestionContextChannel } from "./QuestionContextChannels";
import type { QuestionContextSelectorTab } from "./QuestionContextSelector";
import type { QuestionContextVideo } from "./QuestionContextVideos";
import {
	handleContextNavigatorKey,
	type QuestionContextNavigator,
} from "./questionContextKeyboard";
import { QUESTION_CONTEXT_VIDEO_PAGE_SIZE } from "./questionContextVideoSearch";
import {
	accountMentionsInRange,
	applyBlockMarkerRule,
	blocksForRange,
	type DocMentionData,
	deleteRange,
	docFromSource,
	docLength,
	handleCandidatesInRange,
	hasAtomInRange,
	type Inline,
	insertInlines,
	insertNewline,
	insertPaste,
	insertText,
	mentionAtCaret,
	outdentBlock,
	plainTextForRange,
	type QuestionDocument,
	questionBoxAccountMentionKey,
	replaceRange,
	selectionBounds,
	sourceTextForRange,
	toAugmentedText,
	toSourceText,
	videoPillsInRange,
	withSelection,
	wordBoundary,
} from "./questionDocument";
import { type QuestionEffort, rememberQuestionEffort } from "./questionEfforts";
import {
	ATOM_SELECTOR,
	type DocumentRenderFactories,
	domOffsetOf,
	domPointAt,
	isAtomicElement,
	renderDocument,
	updateActiveMarkdownToken as revealActiveMarkdownToken,
} from "./questionMarkdownDecorator";
import {
	getRecentCompletions,
	saveRecentCompletion,
} from "./RecentAccountCompletions";
import { saveRecentVideoMention } from "./RecentVideoMentions";

function getQuestionFromSearchParams(): string {
	return (
		new URLSearchParams(window.location.search).get("question")?.trim() ?? ""
	);
}

/** `@` + handle + one or more whitespace (spaces, NBSP after picker, etc.); no `g` so `.test()` is stateless. */
const mentionRegex = /@([^\s]+)/;

/** Characters that terminate a mention. Everything else is valid inside a handle. */
export const MENTION_DELIMITER = /[\s,;:!?"'()[\]{}<>/\\|#$%^&*+=~`]/;

const MENTION_DEBOUNCE_MS = 250;

/** Distinct handles one paste may look up when promoting `@handle` text to chips. */
const MAX_PASTE_HANDLE_LOOKUPS = 8;

/** Video pills one paste may fetch a preview frame for. */
const MAX_PASTE_THUMBNAIL_LOOKUPS = 8;

/** Upper bound on retained undo/redo snapshots (guards against unbounded memory). */
const MAX_HISTORY_ENTRIES = 200;
/**
 * Consecutive typing commits within this window collapse into one undo step, so
 * a burst of keystrokes reverts together rather than one character at a time
 * (mirroring native contenteditable undo granularity).
 */
const TYPING_COALESCE_MS = 600;

/**
 * Why a history snapshot was taken. Only `type` commits coalesce with each
 * other, and `hydrate` folds into whatever step is already current (it stands
 * for a presentational fill, such as a pill's preview frame arriving, which the
 * user should not have to undo). Every other reason forms its own undo step.
 */
type CommitReason =
	| "type"
	| "insert"
	| "paste"
	| "delete"
	| "cut"
	| "mention"
	| "speech"
	| "hydrate";

/**
 * Zero-width space the decorator places on both sides of an atom (video-mention
 * pill or account-mention chip): WebKit/Safari can't render the caret directly
 * adjacent to an atomic (contenteditable=false) element, so a real (zero-width)
 * text node gives it a place to land. The atom-caret helpers below treat these
 * anchors as invisible.
 *
 * True when `node` is a text node containing only zero-width caret anchors.
 */
const isZeroWidthText = (node: Node | null | undefined): node is Text =>
	node != null &&
	node.nodeType === Node.TEXT_NODE &&
	/^[\u200B\u200C\uFEFF]+$/.test(node.textContent ?? "");

const videoMentionKeysInDoc = (document: QuestionDocument): string[] => {
	const keys: string[] = [];
	for (const block of document.blocks) {
		for (const inline of block.inlines) {
			if (inline.kind === "pill") {
				keys.push(questionBoxVideoMentionKey(inline.data));
			}
		}
	}
	return keys;
};

const accountMentionKeysInDoc = (document: QuestionDocument): string[] => {
	const keys: string[] = [];
	for (const block of document.blocks) {
		for (const inline of block.inlines) {
			if (inline.kind === "mention") {
				keys.push(questionBoxAccountMentionKey(inline.data));
			}
		}
	}
	return keys;
};

export interface QuestionBoxHandle {
	focus: () => void;
	blur: () => void;
	clear: () => void;
	getText: () => string;
	/** Insert video-mention pills at the current (or last-known) caret position. */
	insertVideoMentions: (mentions: QuestionBoxVideoMentionData[]) => void;
}

export type QuestionBoxScope =
	| "public_pool"
	| "influencer_pool"
	| "project"
	| "agency"
	| "subscriptions";

export type QuestionBoxProps = {
	onAsk?: (
		prompt: string,
		clearQuestion: () => void,
		selectedPoolId: string,
	) => void;
	placeholder?: string;
	/**
	 * Sent with a question the box submits itself; with `onAsk`, the caller
	 * sends it. @default "auto"
	 */
	effort?: QuestionEffort;
	/**
	 * Whether asking makes `effort` the viewer's default. Off for an effort the
	 * viewer didn't choose. @default true
	 */
	rememberEffort?: boolean;
	buttonLabel?: string;
	buttonSymbol?: string;
	buttonTheme?: ButtonTheme;
	/**
	 * Called in place of the "Please enter a question" toast when an empty
	 * question is submitted. While it is set the submit button stays enabled
	 * with nothing typed, so the button can stand for something other than
	 * asking (joining a members-only page, say).
	 */
	onEmptySubmit?: () => void;
	/** Called with the question's text whenever it changes. */
	onQuestionChange?: (question: string) => void;
	disabled?: boolean;
	/**
	 * Whether the question can mention accounts and videos. Off, the context
	 * selector is not offered, `@` is plain text, and a pasted or seeded
	 * mention arrives as its `@handle` or label rather than as a chip.
	 */
	mentions?: boolean;
	/**
	 * Disables the question and the controls that shape it, but leaves the
	 * submit button live, for a box whose button does something other than ask
	 * (joining a members-only AskPage).
	 */
	inputLocked?: boolean;
	onReady?: (handle: QuestionBoxHandle) => void;
	variant?: "default" | "reply";
	isLoading?: boolean;
	completionType?: "influencer" | "social_account";
	includeRecentCompletions?: boolean;
	question?: string;
	/** Focus the input on mount, caret after the seeded `question`. */
	autofocus?: boolean;
	poolId?: string;
	/** Display name of `poolId`, used when the empty context label names the pool. */
	poolName?: string;
	projectId?: string;
	scope: QuestionBoxScope;
	redirectURL: string;
	theme?: "labs" | "studio";
	publicPools?: { id: string; name: string }[];
	/** The default public pool ID (main pool for AskGVT). */
	defaultPoolId?: string;
	requiresPoolSelection?: boolean;
	agencyPublicId?: string;
	/**
	 * Clamp the input's growth to this many text lines, after which it scrolls
	 * vertically. When omitted the input grows without an internal limit.
	 */
	maxLines?: number;
	/** Tabs the context selector offers, in the order given. */
	contextTabs: QuestionContextSelectorTab[];
	/**
	 * Langchain artifact attached as the only context on a new conversation
	 * (from saved-answer handoff). Omitted for normal asks.
	 */
	contextArtifactId?: string;
	/** Resolves artifact id from the context row (handoff answers carry `artifactId`). */
	getContextArtifactId?: () => string | undefined;
	/** Pre-selected saved answer shown in the context row (e.g. answers-page handoff). */
	initialContextAnswer?: QuestionContextAnswer;
	/**
	 * Heading of the Channels tab, named for what the tab lists on that page:
	 * Studio's AskPages and question chat list the creator's connected
	 * sources, AskGVT's AskPage the creators the user subscribes to.
	 * @default "Channels"
	 */
	channelsTabLabel?: "Channels" | "Sources" | "Creators";
	/**
	 * Where the context selector's rows come from before a handle has been
	 * typed. `conversation`, the default, offers what the conversation has
	 * mentioned so far, which is how a reply box fills its tabs. `pool` browses
	 * the box's own pool instead, for a box that starts a conversation rather
	 * than continuing one: it has nothing mentioned to draw on, so without this
	 * its tabs are empty until a handle narrows them.
	 */
	contextSource?: "conversation" | "pool";
	/** Mentioned videos from the current conversation, shown in the context selector. */
	contextVideos?: QuestionContextVideo[];
	/**
	 * Whether the Videos tab may search for videos beyond `contextVideos`. Off,
	 * a typed handle only narrows what the conversation has mentioned.
	 * @default true
	 */
	searchesContextVideos?: boolean;
	/** Mentioned accounts from the current conversation, shown in the context selector. */
	contextChannels?: QuestionContextChannel[];
	/**
	 * Pool whose videos the context selector may offer, for a box that has no
	 * pool of its own to ask against: a chat reply box takes this from the
	 * question the conversation belongs to.
	 */
	contextPoolId?: string;
	/**
	 * Whose videos and creators the context selector offers, for a box whose
	 * context is not a pool it could name: one creator's AskPage, or every
	 * creator the viewer subscribes to. Resolved server-side, like the
	 * question scopes of the same names.
	 */
	contextCreators?: { influencerId: string } | "subscriptions";
	/** Creator handle for public AskPage video search (guests). */
	askPageHandle?: string;
	/** Which video search API the AskPage mention picker uses. */
	askPageVideoSearch?: "public" | "authenticated";
	/**
	 * Question the conversation belongs to, so that the context selector can
	 * offer and flag its answers. Absent until a question exists, which is the
	 * case for every box that starts one.
	 */
	contextQuestionId?: string;
};

export function useQuestionBox(props: QuestionBoxProps) {
	let inputRef: HTMLDivElement | undefined;
	let wrapperRef: HTMLDivElement | undefined;
	let mentionDebounceTimer: ReturnType<typeof setTimeout> | undefined;
	/** Survives closing the context selector so Sources refetch is not needed on reopen. */
	const completionsByKey = new Map<string, AccountCompletionItem[]>();

	/** Reveal the marker(s) of the Markdown token under the caret. */
	const updateActiveMarkdownToken = () => {
		if (inputRef) revealActiveMarkdownToken(inputRef);
	};

	/** Guards against re-entrant commits while a snapshot is being applied. */
	let isRestoring = false;

	/** Re-baselines history to the current state, discarding undo/redo. */
	const resetHistory = () => {
		if (inputRef) resetModelHistory();
	};

	const undo = () => undoModel();
	const redo = () => redoModel();

	const mentionsEnabled = () => props.mentions !== false;

	/**
	 * Source text as this box will take it. Parsing is what turns mention JSON
	 * into atoms, so where mentions are off the text is parsed once and written
	 * back out with each atom as its readable form.
	 */
	const acceptedSource = (source: string): string =>
		mentionsEnabled() ? source : toSourceText(docFromSource(source));

	// --- Controlled document model -----------------------------------------
	// The editor is model-authoritative: `doc` is the source of truth, every
	// edit is a pure operation on it, and the DOM is rendered from the model
	// with the caret placed from model offsets. This removes the fragile "read
	// the caret back out of the rebuilt DOM" round trip that made list/newline
	// editing non-deterministic. Undo/redo, mention detection and serialisation
	// all read the model rather than the DOM.
	let doc: QuestionDocument = docFromSource(
		acceptedSource(props.question?.trim() || getQuestionFromSearchParams()),
	);
	const [insertedVideoMentionKeys, setInsertedVideoMentionKeys] = createSignal(
		videoMentionKeysInDoc(doc),
	);
	const [insertedAccountMentionKeys, setInsertedAccountMentionKeys] =
		createSignal(accountMentionKeysInDoc(doc));
	const syncInsertedContextKeys = () => {
		setInsertedVideoMentionKeys(videoMentionKeysInDoc(doc));
		setInsertedAccountMentionKeys(accountMentionKeysInDoc(doc));
	};
	let composing = false;
	/** Model offsets of the range being replaced by an in-flight IME composition. */
	let compositionRange: [number, number] | null = null;

	const modelUndoStack: QuestionDocument[] = [];
	const modelRedoStack: QuestionDocument[] = [];
	let modelCurrent: QuestionDocument | null = null;
	let modelLastReason: CommitReason | "reset" | "history" = "reset";
	let modelLastTime = 0;

	const renderFactories: DocumentRenderFactories = {
		createPill: (data) =>
			createQuestionBoxVideoMention(data, { onRemove: removeAtomFromModel }),
		createMention: (data) =>
			createQuestionBoxAccountMention(data, { onRemove: removeAtomFromModel }),
	};

	const renderDoc = () => {
		if (inputRef) renderDocument(inputRef, doc, renderFactories);
	};

	/**
	 * Scrolls the input so the *caret* is visible after a model render. We rebuild
	 * the whole view with `replaceChildren` (which resets `scrollTop`), so we must
	 * re-anchor on the caret — never on the trailing filler `<br>` (it lives at the
	 * bottom of the content, so scrolling to it would jump away from a caret that
	 * sits mid-document, e.g. after inserting a newline or deleting a pill).
	 */
	const scrollModelCaretIntoView = () => {
		const el = inputRef;
		if (!el) return;
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return;

		const caret = selection.getRangeAt(0).cloneRange();
		caret.collapse(false);

		// Prefer the caret's own rect; a collapsed caret can report an empty rect
		// in Safari (notably on empty lines), so fall back to the exact node it
		// sits at — the child at the caret offset when it's anchored on the input
		// element itself (selecting the whole element would measure the bottom).
		const caretRect = caret.getBoundingClientRect();
		if (
			caretRect.height === 0 &&
			caretRect.top === 0 &&
			caretRect.bottom === 0
		) {
			let node: Node | null = caret.startContainer;
			if (node === el) {
				node =
					el.childNodes[caret.startOffset] ??
					el.childNodes[caret.startOffset - 1] ??
					null;
			}
			if (node) scrollNodeIntoView(node);
			return;
		}

		const view = el.getBoundingClientRect();
		if (caretRect.bottom > view.bottom) {
			el.scrollTop += caretRect.bottom - view.bottom;
		} else if (caretRect.top < view.top) {
			el.scrollTop -= view.top - caretRect.top;
		}
	};

	/** Records a model history step, coalescing consecutive typing bursts. */
	const commitModel = (reason: CommitReason) => {
		if (isRestoring) return;
		// Absorbed into the step in progress, so undo skips past it to the edit the
		// user actually made — and doesn't come back to a stale version of it.
		if (reason === "hydrate") {
			modelCurrent = doc;
			return;
		}
		if (!modelCurrent) {
			modelCurrent = doc;
			modelLastReason = reason;
			modelLastTime = Date.now();
			return;
		}
		if (modelCurrent === doc) return;
		const now = Date.now();
		const coalesce =
			reason === "type" &&
			modelLastReason === "type" &&
			now - modelLastTime < TYPING_COALESCE_MS;
		if (!coalesce) {
			modelUndoStack.push(modelCurrent);
			if (modelUndoStack.length > MAX_HISTORY_ENTRIES) modelUndoStack.shift();
			modelRedoStack.length = 0;
		}
		modelCurrent = doc;
		modelLastReason = reason;
		modelLastTime = now;
	};

	const resetModelHistory = () => {
		modelUndoStack.length = 0;
		modelRedoStack.length = 0;
		modelCurrent = doc;
		modelLastReason = "reset";
		modelLastTime = 0;
	};

	/** Applies a new document, renders it, and records a history step. */
	const applyDoc = (
		next: QuestionDocument,
		reason: CommitReason,
		options?: { search?: boolean },
	) => {
		doc = next;
		renderDoc();
		setQuestion(toSourceText(doc));
		syncInsertedContextKeys();
		scheduleInputLineLayoutUpdate();
		updateActiveMarkdownToken();
		scrollModelCaretIntoView();
		if (options?.search) {
			setHighlightedIndex(-1);
			handleMentionSearchModel();
		} else {
			dismissCompletions();
		}
		commitModel(reason);
	};

	const restoreModel = (next: QuestionDocument) => {
		isRestoring = true;
		doc = next;
		inputRef?.focus();
		renderDoc();
		setQuestion(toSourceText(doc));
		syncInsertedContextKeys();
		dismissCompletions();
		scheduleInputLineLayoutUpdate();
		updateActiveMarkdownToken();
		isRestoring = false;
	};

	const undoModel = () => {
		if (modelUndoStack.length === 0 || !modelCurrent) return;
		const previous = modelUndoStack.pop() as QuestionDocument;
		modelRedoStack.push(modelCurrent);
		modelCurrent = previous;
		modelLastReason = "history";
		restoreModel(previous);
	};

	const redoModel = () => {
		if (modelRedoStack.length === 0 || !modelCurrent) return;
		const next = modelRedoStack.pop() as QuestionDocument;
		modelUndoStack.push(modelCurrent);
		modelCurrent = next;
		modelLastReason = "history";
		restoreModel(next);
	};

	/** Syncs `doc.selection` from the live DOM selection (mouse, arrows, clicks). */
	const syncModelFromDom = () => {
		if (!inputRef || composing) return;
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return;
		const range = selection.getRangeAt(0);
		if (
			!inputRef.contains(range.startContainer) ||
			!inputRef.contains(range.endContainer)
		) {
			return;
		}
		const anchor = domOffsetOf(
			inputRef,
			range.startContainer,
			range.startOffset,
		);
		const focus = range.collapsed
			? anchor
			: domOffsetOf(inputRef, range.endContainer, range.endOffset);
		doc = withSelection(doc, { anchor, focus });
	};

	/** Removes the atom (pill or mention chip) at `element` via a model delete. */
	const removeAtomFromModel = (element: HTMLElement) => {
		if (!inputRef) return;
		const parent = element.parentNode;
		if (!parent) return;
		const index = Array.prototype.indexOf.call(parent.childNodes, element);
		const start = domOffsetOf(inputRef, parent, index);
		applyDoc(deleteRange(doc, start, start + 1), "delete");
		inputRef.focus();
	};

	/** Inserts a line break at the model caret (Shift+Enter), list-aware. */
	const insertNewlineModel = () => {
		syncModelFromDom();
		applyDoc(insertNewline(doc), "insert");
	};

	const handleMentionSearchModel = () => {
		// Spaces and punctuation stay in the query only while the selector is
		// already up. Typed after it was dismissed, they still end the mention,
		// so the selector stays dismissed — except when the caret is still inside
		// one (e.g. after clicking back into `@foo bar`).
		const phraseSearch =
			contextSelectorOpen() || mentionAtCaret(doc, { phrase: true }) !== null;
		const mention = mentionsEnabled()
			? mentionAtCaret(doc, { phrase: phraseSearch })
			: null;
		if (!mention) {
			dismissCompletions();
			return;
		}
		// A contenteditable may insert a nbsp for the space bar. The search is
		// matched against ordinary spaces in titles.
		const partial = mention.partial.replaceAll("\u00A0", " ");
		setMentionQuery(partial);
		showMentionCompletions(partial);
	};

	/**
	 * The range a word delete should really cover. The browser's own word
	 * segmentation can't see our atoms, so Option-Backspace beside a run of
	 * adjacent pills asks to take the whole run; whenever its range crosses an
	 * atom, the model's boundary is substituted, where one atom is one word. A
	 * delete that replaces a selection is left alone — that isn't a word move.
	 */
	const wordDeleteBounds = (
		inputType: string,
		backward: boolean,
		from: number,
		to: number,
	): [number, number] => {
		if (!inputType.startsWith("deleteWord")) return [from, to];
		const [selFrom, selTo] = selectionBounds(doc.selection);
		if (selFrom !== selTo) return [from, to];
		if (!hasAtomInRange(doc, from, to)) return [from, to];
		return backward
			? [wordBoundary(doc, to, "left"), to]
			: [from, wordBoundary(doc, from, "right")];
	};

	/** Deletes the range the browser intended (word/line/char) from the model. */
	const applyDeleteFromEvent = (event: InputEvent, backward: boolean) => {
		// Backspace at the very start of a heading/quote/list item outdents it back
		// to a plain paragraph (dropping the marker) rather than merging lines.
		if (backward) {
			const outdented = outdentBlock(doc);
			if (outdented) {
				applyDoc(outdented, "delete", { search: true });
				return;
			}
		}
		const ranges = event.getTargetRanges?.() ?? [];
		if (ranges.length > 0 && inputRef) {
			const range = ranges[0];
			const from = domOffsetOf(
				inputRef,
				range.startContainer,
				range.startOffset,
			);
			const to = domOffsetOf(inputRef, range.endContainer, range.endOffset);
			if (from !== to) {
				const [lo, hi] = wordDeleteBounds(event.inputType, backward, from, to);
				applyDoc(deleteRange(doc, lo, hi), "delete", { search: true });
				return;
			}
		}
		applyDoc(
			backward
				? deleteRange(doc, ...backwardUnit())
				: deleteRange(doc, ...forwardUnit()),
			"delete",
			{ search: true },
		);
	};

	const backwardUnit = (): [number, number] => {
		const [from, to] = selectionBounds(doc.selection);
		return from !== to ? [from, to] : [Math.max(0, from - 1), from];
	};
	const forwardUnit = (): [number, number] => {
		const [from, to] = selectionBounds(doc.selection);
		return from !== to ? [from, to] : [to, to + 1];
	};

	/**
	 * The controlled-input choke point. Every non-composition edit is cancelled
	 * and re-expressed as a document operation; IME composition is left to the
	 * browser and reconciled on `compositionend`.
	 */
	const handleBeforeInput = (event: InputEvent) => {
		if (!inputRef) return;
		const type = event.inputType;
		if (event.isComposing || type.startsWith("insertComposition")) return;

		syncModelFromDom();

		switch (type) {
			case "insertText":
			case "insertReplacementText": {
				event.preventDefault();
				const text = event.data ?? "";
				if (type === "insertReplacementText") {
					const ranges = event.getTargetRanges?.() ?? [];
					if (ranges.length > 0) {
						const range = ranges[0];
						const from = domOffsetOf(
							inputRef,
							range.startContainer,
							range.startOffset,
						);
						const to = domOffsetOf(
							inputRef,
							range.endContainer,
							range.endOffset,
						);
						applyDoc(
							replaceRange(doc, from, to, [{ kind: "text", value: text }]),
							"type",
							{
								search: true,
							},
						);
						break;
					}
				}
				// After typing, a completed block marker at the line start (e.g. `- `,
				// `## `, `1. `) transforms the block into that structural kind and
				// removes the literal marker text.
				const typed = insertText(doc, text);
				applyDoc(applyBlockMarkerRule(typed) ?? typed, "type", {
					search: true,
				});
				break;
			}
			case "insertParagraph": {
				event.preventDefault();
				void submit();
				break;
			}
			case "insertLineBreak": {
				event.preventDefault();
				insertNewlineModel();
				break;
			}
			case "insertFromPaste":
			case "insertFromDrop": {
				event.preventDefault();
				const text = event.dataTransfer?.getData("text/plain") ?? "";
				applyDoc(insertPaste(doc, acceptedSource(text)), "paste", {
					search: mentionsEnabled(),
				});
				break;
			}
			case "deleteContentBackward":
			case "deleteWordBackward":
			case "deleteSoftLineBackward":
			case "deleteHardLineBackward": {
				event.preventDefault();
				applyDeleteFromEvent(event, true);
				break;
			}
			case "deleteContent":
			case "deleteContentForward":
			case "deleteWordForward":
			case "deleteSoftLineForward":
			case "deleteHardLineForward":
			case "deleteByCut":
			case "deleteByDrag": {
				event.preventDefault();
				applyDeleteFromEvent(event, false);
				break;
			}
			case "historyUndo": {
				event.preventDefault();
				undoModel();
				break;
			}
			case "historyRedo": {
				event.preventDefault();
				redoModel();
				break;
			}
			default: {
				// Keep the model authoritative: cancel anything we don't model.
				event.preventDefault();
			}
		}
	};

	const handleCompositionStart = () => {
		syncModelFromDom();
		composing = true;
		compositionRange = selectionBounds(doc.selection);
	};

	const handleCompositionEnd = (event: CompositionEvent) => {
		composing = false;
		const range = compositionRange;
		compositionRange = null;
		if (!range) return;
		const [from, to] = range;
		// Rebuild from the model (discarding the browser's composition DOM) with
		// the committed text spliced in at the original composition range.
		applyDoc(
			replaceRange(doc, from, to, [{ kind: "text", value: event.data ?? "" }]),
			"type",
			{ search: true },
		);
	};
	// -----------------------------------------------------------------------

	const assignInputRef = (el: HTMLDivElement | undefined) => {
		inputResizeObserver?.disconnect();
		inputResizeObserver = undefined;
		inputRef = el;
		if (!el) return;

		inputResizeObserver = new ResizeObserver(scheduleInputLineLayoutUpdate);
		inputResizeObserver.observe(el);

		const next = props.question?.trim() || getQuestionFromSearchParams();
		doc = docFromSource(acceptedSource(next ?? ""));
		renderDoc();
		setQuestion(toSourceText(doc));
		scheduleInputLineLayoutUpdate();
		resetHistory();
		// A seeded prompt's chips carry no avatar; resolve them from their ids.
		void resolveMentionAvatars(0, docLength(doc));
	};

	const [selectedProjectId, setSelectedProjectId] = createSignal<string>(
		props.projectId ?? "",
	);
	const [selectedPoolId, setSelectedPoolId] = createSignal<string>(
		props.requiresPoolSelection
			? (props.poolId ?? props.defaultPoolId ?? "")
			: (props.poolId ??
					props.defaultPoolId ??
					props.publicPools?.[0]?.id ??
					""),
	);

	const influencerSearchQuery = (q: string): SearchInfluencersData["query"] => {
		if (props.contextCreators === "subscriptions") {
			return { q, scope: "subscriptions" };
		}
		if (props.scope === "agency" && selectedPoolId().length > 0) {
			return { q, group_id: selectedPoolId() };
		}
		return { q, pool_id: selectedPoolId() };
	};

	const initialQuestion = () =>
		acceptedSource(props.question?.trim() || getQuestionFromSearchParams());
	const [question, setQuestion] = createSignal<string>(initialQuestion());
	createEffect(on(question, (next) => props.onQuestionChange?.(next)));
	const [completions, setCompletions] = createSignal<AccountCompletionItem[]>(
		[],
	);
	/**
	 * The handle being written after an `@`, or null when the caret is not in one.
	 * This is what puts the context selector on screen (on its Channels tab):
	 * from the bare `@` onwards, before any search has returned, and again on the
	 * next keystroke if the selector was dismissed mid-handle.
	 */
	const [mentionQuery, setMentionQuery] = createSignal<string | null>(null);
	/**
	 * Whether a row has been taken from the context selector since it opened.
	 * The selector stays on screen after a pick, so that more can be taken, but
	 * the pick ends the `@handle` search: the next printing key goes into the
	 * question and takes the selector away.
	 */
	const [pickedFromContext, setPickedFromContext] = createSignal(false);
	const contextSelectorOpen = () =>
		mentionQuery() !== null || pickedFromContext();
	const [highlightedIndex, setHighlightedIndex] = createSignal(-1);
	const [isSingleLineInput, setIsSingleLineInput] = createSignal(true);

	let inputResizeObserver: ResizeObserver | undefined;
	let speechAnchorText = "";

	const updateInputLineLayout = () => {
		const el = inputRef;
		if (!el) {
			setIsSingleLineInput(true);
			return;
		}

		const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
		if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
			setIsSingleLineInput(!/\n/.test(el.innerText));
			return;
		}

		setIsSingleLineInput(el.scrollHeight <= lineHeight + 1);
	};

	const scheduleInputLineLayoutUpdate = () => {
		requestAnimationFrame(updateInputLineLayout);
	};

	effect(() => {
		const next = acceptedSource(props.question ?? "");
		setQuestion(next);
		const el = inputRef;
		if (!el) return;
		if (toSourceText(doc) === next) return;
		doc = docFromSource(next);
		renderDoc();
		setQuestion(toSourceText(doc));
		scheduleInputLineLayoutUpdate();
		resetHistory();
		void resolveMentionAvatars(0, docLength(doc));
	});

	const completionsCacheKey = (query: string) =>
		`${props.completionType ?? "social"}|${selectedPoolId()}|${selectedProjectId()}|${query}`;

	const clearCompletions = () => {
		setCompletions([]);
		setHighlightedIndex(-1);
	};

	/** Clears the completions and takes the selector back off screen. */
	const dismissCompletions = () => {
		if (mentionDebounceTimer !== undefined) clearTimeout(mentionDebounceTimer);
		clearCompletions();
		setMentionQuery(null);
		setPickedFromContext(false);
	};

	const isProjectSelectorDisabled = () => mentionRegex.test(question());

	/**
	 * Whether an empty handle is worth searching for. The searches read an empty
	 * `q` as "browse this scope" rather than as no query at all, so a bare `@`
	 * can offer the pool's own accounts and videos — but only where the box was
	 * asked to browse, since anywhere else the tabs have the conversation's
	 * mentions to show and a browse would push them out of the way.
	 */
	const browsesContextPool = () =>
		props.contextSource === "pool" && props.searchesContextVideos !== false;

	/**
	 * Searches the accounts that can be mentioned, scoped to the current
	 * pool/project. A box without a Channels tab has no accounts to offer, so
	 * a typed `@handle` must not turn into one on a delimiter or a paste either.
	 */
	const searchAccounts = async (
		partial: string,
	): Promise<AccountCompletionItem[]> => {
		if (!props.contextTabs.includes("channels")) return [];
		const { data } =
			props.completionType === "influencer"
				? await searchInfluencers({ query: influencerSearchQuery(partial) })
				: await searchSocialChannels({
						query: {
							q: partial,
							pool_id: selectedPoolId(),
							project_id: selectedProjectId(),
						},
					});
		return data?.results ?? [];
	};

	const videoSearchQuery = (q: string): SearchVideosData["query"] => {
		const creators = props.contextCreators;
		if (creators === "subscriptions") {
			return { q, scope: "subscriptions" };
		}
		if (creators) {
			return { q, influencer_id: creators.influencerId };
		}
		return {
			q,
			pool_id: selectedPoolId() || props.contextPoolId,
			project_id: selectedProjectId(),
		};
	};

	/** A search hit as a row: a whole video, so with no clip range of its own. */
	const contextVideoFromSearchResult = (
		result: VideoSearchResult,
	): QuestionContextVideo => ({
		id: result.id ?? "",
		title: result.title?.trim() || "Untitled video",
		thumbnailUrl: result.thumbnail_url,
		durationSec: result.duration_seconds,
		handle: result.creator_handle ?? result.account_handle,
		startTimeSec: null,
		endTimeSec: null,
	});

	/**
	 * Searches the videos that can be mentioned, scoped like the accounts above.
	 * The context selector's Videos tab asks for these as the `@handle` is typed;
	 * the scoping lives here, with the pool and project the question is asked of.
	 *
	 * Sending no pool and no project is meaningful: the endpoint then searches
	 * the public pool, which is what a box with nothing else to go on — an
	 * AskGVT question — is asking about.
	 */
	const searchContextVideos = async (partial: string, page: number) => {
		if (props.searchesContextVideos === false) {
			return { videos: [], total: 0 };
		}
		const q = partial.trim();
		if (q.length === 0 && !browsesContextPool()) {
			return { videos: [], total: 0 };
		}
		const pageSize = QUESTION_CONTEXT_VIDEO_PAGE_SIZE;
		try {
			if (props.askPageVideoSearch === "public" && props.askPageHandle) {
				const { data } = await searchPublicAskPageVideos({
					path: { handle: props.askPageHandle },
					query: {
						q: q.length > 0 ? q : undefined,
						page,
						page_size: pageSize,
					},
				});
				return {
					videos: (data?.results ?? []).map(contextVideoFromSearchResult),
					total: data?.total ?? 0,
				};
			}
			const { data } = await searchVideos({
				query: {
					...videoSearchQuery(q),
					page,
					page_size: pageSize,
				},
			});
			return {
				videos: (data?.results ?? []).map(contextVideoFromSearchResult),
				total: data?.total ?? 0,
			};
		} catch (err) {
			console.error("video search failed", err);
			return { videos: [], total: 0 };
		}
	};

	/** The exact-handle match in `accounts`, if there is one. */
	const exactHandleMatch = (
		accounts: AccountCompletionItem[],
		handle: string,
	): AccountCompletionItem | undefined =>
		accounts.find((a) => (a.handle ?? "").toLowerCase() === handle);

	const mentionChip = (account: AccountCompletionItem): Inline => ({
		kind: "mention",
		data: {
			type: account.type,
			handle: account.handle ?? "",
			platform:
				account.type === "social_account" && account.platform
					? account.platform
					: undefined,
			id: account.id ?? undefined,
			avatarUrl: account.profile_picture_url,
		},
	});

	/**
	 * Accepts an `@`-completion by replacing the typed partial with a chip atom,
	 * or by writing the chip at the caret when no handle is being typed — a
	 * further pick from a selector whose search an earlier pick ended.
	 */
	const acceptCompletion = (
		account: AccountCompletionItem,
		trailingSeparator = "\u00A0",
	) => {
		const mention = mentionAtCaret(doc, {
			phrase: contextSelectorOpen(),
		});
		const inlines: Inline[] = [
			mentionChip(account),
			{ kind: "text", value: trailingSeparator },
		];
		applyDoc(
			mention
				? replaceRange(doc, mention.start, mention.end, inlines)
				: insertInlines(doc, inlines),
			"mention",
		);
		inputRef?.focus();
		saveRecentCompletion(account);
	};

	/**
	 * Populates the completion list for a partial `@handle`. An empty list leaves
	 * the selector showing the accounts already mentioned in the conversation, so
	 * a handle that matches nothing is not a dead end.
	 */
	const showMentionCompletions = (partial: string) => {
		if (mentionDebounceTimer !== undefined) clearTimeout(mentionDebounceTimer);

		// A trailing space is kept in the question so the selector stays open
		// between the words of a phrase. It is not part of the handle to look up.
		const query = partial.trim();

		if (!query && props.includeRecentCompletions === true) {
			const recent = getRecentCompletions(props.completionType);
			setCompletions(recent);
			setHighlightedIndex(recent.length > 0 ? 0 : -1);
			return;
		}

		// A bare `@` is only worth a request where the pool can be browsed for
		// what it could become; elsewhere the tab keeps the conversation's own
		// accounts until a handle is there to narrow them.
		if (!query && !browsesContextPool()) {
			clearCompletions();
			return;
		}

		const cacheKey = completionsCacheKey(query);
		const cached = completionsByKey.get(cacheKey);
		if (cached !== undefined) {
			setCompletions(cached);
			setHighlightedIndex(cached.length > 0 ? 0 : -1);
			return;
		}

		mentionDebounceTimer = setTimeout(async () => {
			try {
				const results = await searchAccounts(query);
				completionsByKey.set(cacheKey, results);
				setCompletions(results);
				setHighlightedIndex(results.length > 0 ? 0 : -1);
			} catch (err) {
				console.error("@mention search failed", err);
				clearCompletions();
			}
		}, MENTION_DEBOUNCE_MS);
	};

	const tryResolveMentionOnDelimiter = async (
		key: string,
		cachedItems: AccountCompletionItem[],
	): Promise<boolean> => {
		const mention = mentionAtCaret(doc, {
			phrase: contextSelectorOpen(),
		});
		if (!mention) return false;

		const typed = mention.partial.toLowerCase();

		let exact = exactHandleMatch(cachedItems, typed);

		if (!exact) {
			if (mentionDebounceTimer !== undefined)
				clearTimeout(mentionDebounceTimer);
			// Nothing has been typed after the `@` yet, so there is no handle to
			// look up: the delimiter just goes in, leaving a free-standing `@`.
			if (mention.partial.length > 0) {
				try {
					exact = exactHandleMatch(
						await searchAccounts(mention.partial),
						typed,
					);
				} catch {
					/* ignore */
				}
			}
		}

		if (exact) {
			const separator = key === " " ? "\u00A0" : key;
			acceptCompletion(exact, separator);
		} else {
			applyDoc(insertText(doc, key), "type", { search: true });
		}

		return true;
	};

	const hideHelpText = () => question() !== "";

	const clearInput = () => {
		doc = docFromSource("");
		renderDoc();
		setQuestion("");
		speechAnchorText = "";
		scheduleInputLineLayoutUpdate();
		resetHistory();
	};

	/**
	 * Scrolls the input so `node` is visible. The browser only auto-scrolls the
	 * caret for native edits, so after a programmatic insertion (paste, mention
	 * pills) the caret can sit below the clamp; this brings it back into view.
	 * Measured via a Range because collapsed-caret rects are unreliable in Safari.
	 */
	const scrollNodeIntoView = (node: Node) => {
		const el = inputRef;
		if (!el) return;
		const range = document.createRange();
		try {
			if (node.nodeType === Node.ELEMENT_NODE) {
				range.selectNode(node);
			} else {
				range.selectNodeContents(node);
			}
		} catch {
			return;
		}
		const rects = range.getClientRects();
		const rect = rects.length > 0 ? rects[rects.length - 1] : null;
		if (!rect) return;
		const view = el.getBoundingClientRect();
		if (rect.bottom > view.bottom) {
			el.scrollTop += rect.bottom - view.bottom;
		} else if (rect.top < view.top) {
			el.scrollTop -= view.top - rect.top;
		}
	};

	/**
	 * Flags atoms (video pills, mention chips) that fall within the current
	 * (non-collapsed) selection so CSS can highlight them, since a
	 * `contenteditable=false` / `user-select: none` element is never part of the
	 * browser's native text-selection highlight.
	 */
	const updateAtomSelectionState = () => {
		if (!inputRef) return;
		const selection = window.getSelection();
		const active =
			selection != null && selection.rangeCount > 0 && !selection.isCollapsed;
		for (const atom of inputRef.querySelectorAll<HTMLElement>(ATOM_SELECTOR)) {
			const selected =
				active && !!selection && selection.containsNode(atom, true);
			atom.classList.toggle(ATOM_SELECTED_CLASS, selected);
		}
	};

	const insertVideoMentions = (mentions: QuestionBoxVideoMentionData[]) => {
		if (!inputRef || mentions.length === 0) return;

		inputRef.focus();
		syncModelFromDom();
		const inlines: Inline[] = [];
		for (const mention of mentions) {
			inlines.push({ kind: "pill", data: mention });
			// Trailing NBSP keeps pills apart and gives the caret a landing spot.
			inlines.push({ kind: "text", value: "\u00A0" });
			saveRecentVideoMention(mention.id);
		}
		// Picked from the context selector while an `@handle` was being typed, the
		// pills stand for what was typed rather than following it — the same rule
		// as accepting a completion.
		const typed = mentionAtCaret(doc, {
			phrase: contextSelectorOpen(),
		});
		applyDoc(
			typed
				? replaceRange(doc, typed.start, typed.end, inlines)
				: insertInlines(doc, inlines),
			"insert",
		);
	};

	/**
	 * Drops the `@handle` being written, putting nothing in its place. For a pick
	 * that belongs somewhere other than the question: an answer joins the context
	 * list below the box, so the question keeps no trace of how it was chosen.
	 */
	const removeMentionAtCaret = () => {
		if (!inputRef) return;
		syncModelFromDom();
		const mention = mentionAtCaret(doc, {
			phrase: contextSelectorOpen(),
		});
		if (!mention) return;
		inputRef.focus();
		applyDoc(deleteRange(doc, mention.start, mention.end), "mention");
	};

	const insertAccountMentions = (mentions: DocMentionData[]) => {
		if (!inputRef || mentions.length === 0) return;

		inputRef.focus();
		syncModelFromDom();
		const inlines: Inline[] = [];
		for (const mention of mentions) {
			inlines.push({ kind: "mention", data: mention });
			inlines.push({ kind: "text", value: "\u00A0" });
		}
		// Picked while an `@handle` was being typed, the chips stand for what was
		// typed rather than following it — the same rule as accepting a completion.
		const typed = mentionAtCaret(doc, {
			phrase: contextSelectorOpen(),
		});
		applyDoc(
			typed
				? replaceRange(doc, typed.start, typed.end, inlines)
				: insertInlines(doc, inlines),
			"mention",
		);
	};

	const writeSelectionToClipboard = (event: ClipboardEvent): boolean => {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
			return false;
		}
		const range = selection.getRangeAt(0);
		if (!inputRef?.contains(range.commonAncestorContainer)) {
			return false;
		}
		// Block markers are structural (not in the DOM), so serialise the selected
		// range from the model to keep them in the copied text.
		syncModelFromDom();
		const [from, to] = selectionBounds(doc.selection);
		event.preventDefault();
		// Two flavours: readable prose for anywhere the text is just read (and for
		// "paste and match style", which strips the HTML), and HTML whose mention
		// links carry the atoms' identity so a prompt survives a round trip through
		// another app. See promptClipboardHtml.
		event.clipboardData?.setData(
			"text/plain",
			plainTextForRange(doc, from, to),
		);
		event.clipboardData?.setData(
			"text/html",
			clipboardHtmlForBlocks(blocksForRange(doc, from, to)),
		);
		return true;
	};

	/**
	 * Copies the prompt in its submitted form — mentions as inline JSON — for
	 * debugging: the selection if there is one, otherwise the whole prompt.
	 * Deliberately on an obscure shortcut rather than in the UI: developers need
	 * to see what the back end will receive, nobody else does, and the result
	 * pastes back into the input as normal atoms.
	 */
	const copyAugmentedPrompt = () => {
		syncModelFromDom();
		const [from, to] = selectionBounds(doc.selection);
		const text =
			from === to
				? promptWithoutVideoThumbnails(toAugmentedText(doc))
				: sourceTextForRange(doc, from, to);
		void navigator.clipboard?.writeText(text);
	};

	const handleCopy = (event: ClipboardEvent) => {
		writeSelectionToClipboard(event);
	};

	const handleCut = (event: ClipboardEvent) => {
		if (!writeSelectionToClipboard(event)) return;
		syncModelFromDom();
		const [from, to] = selectionBounds(doc.selection);
		applyDoc(deleteRange(doc, from, to), "cut");
	};

	const beginSpeechSession = () => {
		speechAnchorText = toSourceText(doc);
		inputRef?.focus();
	};

	const endSpeechSession = () => {
		speechAnchorText = toSourceText(doc);
	};

	const applySpeechTranscript = (transcript: string, isFinal: boolean) => {
		if (!inputRef) return;

		dismissCompletions();

		const trimmed = transcript.trim();
		if (isFinal && !trimmed) return;
		const separator =
			speechAnchorText.length > 0 && !/\s$/.test(speechAnchorText) ? " " : "";
		const full = `${speechAnchorText}${separator}${isFinal ? trimmed : transcript}`;
		if (isFinal) speechAnchorText = full;
		doc = docFromSource(full);
		renderDoc();
		setQuestion(toSourceText(doc));
		scheduleInputLineLayoutUpdate();
		updateActiveMarkdownToken();
		// Commit only finalised dictation, so a single undo removes the whole
		// utterance rather than each streamed interim fragment.
		if (isFinal) commitModel("speech");
	};

	const submit = async () => {
		if (isDisabled()) return;
		const sourceText = toSourceText(doc);
		if (!inputRef || sourceText.trim().length === 0) {
			if (props.onEmptySubmit) {
				props.onEmptySubmit();
				return;
			}
			toast.info({
				title: "Please enter a question",
			});
			return;
		}
		dismissCompletions();
		const prompt = toAugmentedText(doc).trim();
		if (props.effort !== undefined && props.rememberEffort !== false) {
			void rememberQuestionEffort(props.effort);
		}

		if (props.onAsk) {
			props.onAsk(prompt, clearInput, selectedPoolId());
			return;
		}

		const getScope = (scope: QuestionBoxScope, poolId: string) => {
			if (scope !== "agency") return scope;
			if (poolId.length === 0) return "agency";
			return "group";
		};
		const scope = getScope(props.scope, selectedPoolId());
		// influencer_pool/subscriptions both resolve their pool(s) server-side
		// and reject a pool_id outright, so it must not be sent —
		// selectedPoolId() still scopes @-mention search for influencer_pool,
		// it just no longer decides which pool answers the question.
		const sendsPoolId =
			scope !== "group" &&
			scope !== "influencer_pool" &&
			scope !== "subscriptions";
		const contextArtifactId =
			props.getContextArtifactId?.()?.trim() ?? props.contextArtifactId?.trim();
		const response = await createQuestion({
			body: {
				scope: scope,
				project_id: scope === "project" ? selectedProjectId() : undefined,
				pool_id: sendsPoolId ? selectedPoolId() : undefined,
				group_id: scope === "group" ? selectedPoolId() : undefined,
				prompt: promptWithoutVideoThumbnails(prompt),
				agency_id: scope === "project" ? undefined : props.agencyPublicId,
				preferred_locale: getPreferredLocale(),
				effort: props.effort,
				...(contextArtifactId
					? { context_artifact_id: contextArtifactId }
					: {}),
			},
		});
		if (response.error) {
			toast.error({
				title: "Failed to start chat",
				description: getErrorMessage(response.error),
			});
			return;
		}
		const path = response.data?.path;
		if (!path) {
			toast.error({
				title: "Failed to start chat",
				description: "Question created but no redirect path was returned.",
			});
			return;
		}
		clearInput();
		window.location.href = path;
	};

	/**
	 * Turns each `@handle` in a freshly pasted range into a mention chip when the
	 * handle resolves to exactly one account in the current pool/project — the
	 * same rule as typing a handle and then a delimiter. This is what makes a
	 * question pasted from somewhere else (a chat transcript, a note, a
	 * spreadsheet column) arrive as real mentions; handles that resolve to
	 * nothing stay as plain text, exactly as typing them does.
	 *
	 * The lookups are asynchronous, so the promotion lands as a second edit (its
	 * own undo step) shortly after the paste. The user's own edits win: the
	 * candidates are re-read from the model once the lookups return, and only
	 * those still sitting at the same offsets are replaced.
	 */
	const promoteHandlesInRange = async (from: number, to: number) => {
		const handles: string[] = [];
		for (const candidate of handleCandidatesInRange(doc, from, to)) {
			const handle = candidate.handle.toLowerCase();
			if (!handles.includes(handle)) handles.push(handle);
			// Bounds the request burst: a paste with more distinct handles than
			// this is prose rather than a deliberate list of accounts.
			if (handles.length === MAX_PASTE_HANDLE_LOOKUPS) break;
		}
		if (handles.length === 0) return;

		const resolved = new Map<string, AccountCompletionItem>();
		await Promise.all(
			handles.map(async (handle) => {
				try {
					const match = exactHandleMatch(await searchAccounts(handle), handle);
					if (match) resolved.set(handle, match);
				} catch {
					/* leave the handle as plain text */
				}
			}),
		);
		if (resolved.size === 0) return;
		// Re-rendering restores the selection, so never reach into an input the
		// user has since left.
		if (!inputRef || document.activeElement !== inputRef) return;

		const promotable = handleCandidatesInRange(doc, from, to).filter((c) =>
			resolved.has(c.handle.toLowerCase()),
		);
		if (promotable.length === 0) return;

		/** Maps a pre-promotion offset through the replacements before it. */
		const shift = (offset: number): number => {
			let result = offset;
			for (const c of promotable) {
				if (offset >= c.end) {
					result -= c.end - c.start - 1;
				} else if (offset > c.start) {
					// The caret sat inside a promoted handle: put it after the chip.
					result -= offset - c.start - 1;
				}
			}
			return result;
		};

		const selection = doc.selection;
		let next = doc;
		// Right to left, so each replacement leaves the earlier offsets valid.
		for (const candidate of [...promotable].reverse()) {
			const account = resolved.get(candidate.handle.toLowerCase());
			if (!account) continue;
			next = replaceRange(next, candidate.start, candidate.end, [
				mentionChip(account),
			]);
			saveRecentCompletion(account);
		}
		applyDoc(
			withSelection(next, {
				anchor: shift(selection.anchor),
				focus: shift(selection.focus),
			}),
			"mention",
		);
	};

	/**
	 * Fetches the preview frame for video pills that arrived without one.
	 *
	 * A pill's thumbnail is a presigned URL: short-lived, and not something to
	 * hand to whoever the prompt was shared with, so neither the mention links nor
	 * a mention resolved by `/mention` carry it. Sampling a frame at the pill's own
	 * timestamp gets a fresh one.
	 */
	const fetchPastedThumbnails = async (from: number, to: number) => {
		const pending = videoPillsInRange(doc, from, to)
			.filter((pill) => !pill.data.thumbnailUrl)
			.slice(0, MAX_PASTE_THUMBNAIL_LOOKUPS);
		if (pending.length === 0) return;

		const thumbnails = new Map<string, string>();
		await Promise.all(
			pending.map(async ({ data }) => {
				// A mention's timestamps describe a moment rather than a range, so
				// fall back to a short window from the start, as the chat does.
				const start = data.startTimeSec ?? 0;
				const end =
					data.endTimeSec != null && data.endTimeSec > start
						? data.endTimeSec
						: start + 10;
				try {
					const response = await getVideoSampledFrames({
						path: { video_id: data.id },
						query: { start, end, num_frames: 1 },
					});
					const url = response.data?.sampled_frames?.[0]?.signed_url;
					if (url) thumbnails.set(data.id, url);
				} catch {
					/* the pill renders without a preview */
				}
			}),
		);
		if (thumbnails.size === 0) return;
		if (!inputRef || document.activeElement !== inputRef) return;

		// Re-read the pills: the user may have edited while the requests were out.
		// An atom is one offset wide, so these swaps leave the selection untouched.
		let next = doc;
		for (const pill of videoPillsInRange(next, from, to)) {
			const thumbnailUrl = thumbnails.get(pill.data.id);
			if (!thumbnailUrl || pill.data.thumbnailUrl) continue;
			next = replaceRange(next, pill.offset, pill.offset + 1, [
				{ kind: "pill", data: { ...pill.data, thumbnailUrl } },
			]);
		}
		applyDoc(withSelection(next, doc.selection), "hydrate");
	};

	/**
	 * Fills in the avatar of account-mention chips that arrived without one.
	 *
	 * A prompt carries a mention's identity and nothing else: pasted or stored,
	 * the JSON holds the id, handle and platform but no picture, deliberately —
	 * the text may be days old and forwarded by someone else, so a display field
	 * it asserted could be stale or made up (see the backend's
	 * AccountMentionResolver). The avatar is therefore resolved from the id once
	 * the text has landed. A chip inserted from a completion or a context row
	 * already has its avatar and is skipped, as is one whose account resolves to
	 * nothing — that chip simply keeps its glyph.
	 *
	 * Unlike the thumbnail pass above, the fill is patched into the chips in place
	 * rather than re-rendered from the model: an avatar arriving is not an edit,
	 * and a stored prompt is usually seeded into a box nobody has focused yet, so
	 * a re-render would take the document's selection away from wherever the user
	 * actually is.
	 */
	const resolveMentionAvatars = async (from: number, to: number) => {
		const pendingIds = accountMentionsInRange(doc, from, to)
			.filter((mention) => mention.data.id && !mention.data.avatarUrl)
			.map((mention) => mention.data.id as string);
		if (pendingIds.length === 0) return;

		const avatars = await resolveAccountMentionAvatars(pendingIds);
		if (avatars.size === 0) return;

		// Re-read the chips, and across the whole document rather than the range
		// asked about: the user may have edited while the request was out, and an
		// avatar belongs to an account, so it belongs to every chip standing for
		// that account. An atom is one offset wide, so these swaps leave the
		// selection untouched.
		let next = doc;
		for (const mention of accountMentionsInRange(next, 0, docLength(next))) {
			const avatarUrl = mention.data.id
				? avatars.get(mention.data.id)
				: undefined;
			if (!avatarUrl || mention.data.avatarUrl) continue;
			next = replaceRange(next, mention.offset, mention.offset + 1, [
				{ kind: "mention", data: { ...mention.data, avatarUrl } },
			]);
		}
		doc = withSelection(next, doc.selection);
		commitModel("hydrate");
		if (inputRef) showAccountMentionAvatars(inputRef, avatars);
	};

	const handlePaste = (event: ClipboardEvent) => {
		event.preventDefault();

		const legacyWindow = window as Window & {
			clipboardData?: { getData: (format: string) => string };
		};
		const clipboardData = event.clipboardData ?? legacyWindow.clipboardData;
		const plainText = clipboardData?.getData("text/plain") ?? "";
		// Prefer the HTML flavour, which is the only one carrying mention identity
		// after a round trip through another app. It declines (returns null) unless
		// it holds one of our mention links, so pasting from anywhere else keeps
		// using the plain text exactly as before.
		const html = clipboardData?.getData("text/html") ?? "";
		const pasted = mentionsEnabled()
			? (augmentedTextFromClipboardHtml(html) ?? plainText)
			: acceptedSource(plainText);

		syncModelFromDom();
		const [pasteStart] = selectionBounds(doc.selection);
		applyDoc(insertPaste(doc, pasted), "paste", {
			search: mentionsEnabled(),
		});
		if (mentionsEnabled()) {
			void promoteHandlesInRange(pasteStart, doc.selection.focus);
		}
		void fetchPastedThumbnails(pasteStart, doc.selection.focus);
		void resolveMentionAvatars(pasteStart, doc.selection.focus);
	};

	/** Shift+Enter: list-aware line break, handled entirely on the model. */
	const insertLineBreak = () => {
		insertNewlineModel();
	};

	const handleInput = () => {
		// The editor is controlled: every edit is applied to the model in
		// `beforeinput` (which we always cancel), and rendering from the model is
		// programmatic, so it fires no `input` event. An `input` event therefore
		// means the browser edited the DOM behind our back — a non-cancelable
		// `beforeinput`, or an edit landing inside an atom's non-editable subtree.
		// Re-render from the model so the view can't keep showing something the
		// document doesn't contain until the next keystroke rebuilds it.
		// Composition is exempt: it is reconciled on `compositionend`.
		if (composing) return;
		renderDoc();
	};

	const hasAtoms = (): boolean =>
		inputRef?.querySelector(ATOM_SELECTOR) != null;

	const collapsedInputRange = (): Range | null => {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
			return null;
		}
		const range = selection.getRangeAt(0);
		return inputRef?.contains(range.startContainer) ? range : null;
	};

	/**
	 * Resolves the caret to a node boundary `{ parent, index }` in the direction
	 * `dir`, treating a run of zero-width anchors between the caret and the node
	 * edge as if it weren't there (so a stranded anchor next to the caret never
	 * blocks a jump). Returns null when real text sits between the caret and the
	 * edge on that side, i.e. the browser should handle the move/delete.
	 */
	const boundaryTowards = (
		range: Range,
		dir: "left" | "right",
	): { parent: Node; index: number } | null => {
		const node = range.startContainer;
		const offset = range.startOffset;
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent ?? "";
			const side = dir === "left" ? text.slice(0, offset) : text.slice(offset);
			if (side.length > 0 && !/^[\u200B\u200C\uFEFF]+$/.test(side)) {
				return null;
			}
			const parent = node.parentNode;
			if (!parent) return null;
			const index = Array.prototype.indexOf.call(parent.childNodes, node);
			return { parent, index: dir === "left" ? index : index + 1 };
		}
		return { parent: node, index: offset };
	};

	/** The atom immediately in `dir` of the caret, skipping zero-width anchors. */
	const adjacentAtom = (
		range: Range,
		dir: "left" | "right",
	): HTMLElement | null => {
		const boundary = boundaryTowards(range, dir);
		if (!boundary) return null;
		const { parent, index } = boundary;
		const step = dir === "left" ? -1 : 1;
		let i = dir === "left" ? index - 1 : index;
		while (i >= 0 && i < parent.childNodes.length) {
			const child = parent.childNodes[i];
			if (
				isZeroWidthText(child) ||
				(child.nodeType === Node.TEXT_NODE && (child.textContent ?? "") === "")
			) {
				i += step;
				continue;
			}
			return child instanceof HTMLElement && isAtomicElement(child)
				? child
				: null;
		}
		return null;
	};

	/**
	 * The position just past an atom in `dir` — landing before any character the
	 * user has typed against the atom but past the atom's own anchor run, so the
	 * edge sits immediately beside the atom with the anchor still between them.
	 */
	const atomCrossTarget = (
		atom: HTMLElement,
		dir: "left" | "right",
	): { node: Node; offset: number } | null => {
		if (dir === "right") {
			const trailing = atom.nextSibling;
			if (trailing && trailing.nodeType === Node.TEXT_NODE) {
				const run = (trailing.textContent ?? "").match(
					/^[\u200B\u200C\uFEFF]*/,
				);
				return { node: trailing, offset: run?.[0].length ?? 0 };
			}
			const parent = atom.parentNode;
			if (!parent) return null;
			const index = Array.prototype.indexOf.call(parent.childNodes, atom);
			return { node: parent, offset: index + 1 };
		}
		const leading = atom.previousSibling;
		if (leading && leading.nodeType === Node.TEXT_NODE) {
			const text = leading.textContent ?? "";
			const run = text.match(/[\u200B\u200C\uFEFF]*$/);
			return { node: leading, offset: text.length - (run?.[0].length ?? 0) };
		}
		const parent = atom.parentNode;
		if (!parent) return null;
		const index = Array.prototype.indexOf.call(parent.childNodes, atom);
		return { node: parent, offset: index };
	};

	/**
	 * Treats an atom (plus its two flanking zero-width anchors) as a single caret
	 * step: if an atom sits immediately in `dir` of the caret, the caret hops to
	 * the far side of it, landing inside the far anchor so Safari still renders
	 * it. Returns false when no atom is adjacent, leaving ordinary one-character
	 * moves (including over the trailing space) to the browser.
	 */
	const moveCaretAcrossAtom = (dir: "left" | "right"): boolean => {
		const range = collapsedInputRange();
		if (!range) return false;
		const atom = adjacentAtom(range, dir);
		if (!atom) return false;
		const selection = window.getSelection();
		if (!selection) return false;
		const target = atomCrossTarget(atom, dir);
		if (!target) return false;

		const collapsed = document.createRange();
		collapsed.setStart(target.node, target.offset);
		collapsed.collapse(true);
		selection.removeAllRanges();
		selection.addRange(collapsed);
		return true;
	};

	/**
	 * Shift+Arrow counterpart of {@link moveCaretAcrossAtom}: extends the
	 * selection's moving edge (focus) across the whole atom+anchors unit in one
	 * step, so selection grows in step with how the caret moves without Shift.
	 * Returns false when no atom is adjacent to the focus (native handles it).
	 */
	const extendSelectionAcrossAtom = (dir: "left" | "right"): boolean => {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return false;
		if (typeof selection.extend !== "function") return false;
		const focusNode = selection.focusNode;
		if (!focusNode || !inputRef?.contains(focusNode)) return false;

		const focusRange = document.createRange();
		focusRange.setStart(focusNode, selection.focusOffset);
		focusRange.collapse(true);
		const atom = adjacentAtom(focusRange, dir);
		if (!atom) return false;
		const target = atomCrossTarget(atom, dir);
		if (!target) return false;

		selection.extend(target.node, target.offset);
		return true;
	};

	/**
	 * Word-wise caret movement (Option-Arrow on a Mac), taken over from the
	 * browser because its word segmentation can't see our atoms: it reads a run
	 * of adjacent pills as a single stretch of non-word content and crosses the
	 * whole run in one step. The model counts each atom as a word of its own, so
	 * moving over a run stops between each pill. Only used while the document
	 * holds atoms — elsewhere the platform's own segmentation is better than
	 * ours. Requires the model caret to be in sync with the DOM.
	 */
	const moveCaretByWord = (dir: "left" | "right", extend: boolean): boolean => {
		if (!inputRef) return false;
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return false;
		const focusNode = selection.focusNode;
		if (!focusNode || !inputRef.contains(focusNode)) return false;
		// The moving edge comes from the DOM, not the model: the model records a
		// selection start-to-end, so it can't say which end Shift is dragging.
		const focus = domOffsetOf(inputRef, focusNode, selection.focusOffset);
		const target = wordBoundary(doc, focus, dir);
		if (target === focus) return false;

		const point = domPointAt(inputRef, target);
		if (extend) {
			if (typeof selection.extend !== "function") return false;
			selection.extend(point.container, point.offset);
			return true;
		}
		const range = document.createRange();
		range.setStart(point.container, point.offset);
		range.collapse(true);
		selection.removeAllRanges();
		selection.addRange(range);
		return true;
	};

	/**
	 * Puts the caret after everything written so far, model and DOM together.
	 * Focusing an input the user has not typed in leaves the caret wherever the
	 * browser decides to put it, which is rarely the end.
	 */
	const placeCaretAtEnd = () => {
		if (!inputRef) return;
		const end = docLength(doc);
		doc = withSelection(doc, { anchor: end, focus: end });
		const selection = window.getSelection();
		if (!selection) return;
		const point = domPointAt(inputRef, end);
		const range = document.createRange();
		range.setStart(point.container, point.offset);
		range.collapse(true);
		selection.removeAllRanges();
		selection.addRange(range);
	};

	/**
	 * The question as it stood before the "+" button wrote its `@` into it, and
	 * the text that writing left behind. Together they say whether the `@` is
	 * still the only trace of the button, and so can be taken back untouched.
	 */
	let pendingContextMention: {
		before: QuestionDocument;
		source: string;
	} | null = null;

	/**
	 * Writes an `@`-mention on behalf of something outside the question — the
	 * context selector's button — so that what it opens is what typing `@` opens:
	 * the caret is back in the question, where the keys that drive the selector
	 * are handled. A disabled box takes no text, and is left as it is.
	 */
	const beginContextMention = () => {
		if (!inputRef || isInputDisabled()) return;

		if (document.activeElement === inputRef) {
			syncModelFromDom();
		} else {
			inputRef.focus();
			placeCaretAtEnd();
		}

		// An `@` already under the caret is the state this is reaching for, so the
		// selector is asked for again rather than a second `@` written — and there
		// is then nothing of the button's own to take back.
		if (mentionAtCaret(doc, { phrase: contextSelectorOpen() })) {
			handleMentionSearchModel();
			return;
		}

		// `@` only begins a mention at a word boundary, so a caret up against a
		// word — the end of "ask me" — needs a space written ahead of it.
		const before = doc;
		const mention = insertText(doc, "@");
		applyDoc(
			mentionAtCaret(mention) ? mention : insertText(doc, " @"),
			"insert",
			{ search: true },
		);
		pendingContextMention = { before, source: toSourceText(doc) };
	};

	/**
	 * Takes the selector off screen with nothing picked out of it, and with it the
	 * `@` the "+" button wrote — so a question the button was clicked over by
	 * mistake is left exactly as the click found it. Only ever the button's own
	 * `@`: a handle typed after it, or a pick that consumed it, has moved the
	 * question on, and what is there is then the user's.
	 */
	const dismissContextMention = () => {
		const pending = pendingContextMention;
		pendingContextMention = null;
		dismissCompletions();
		if (pending && toSourceText(doc) === pending.source) {
			applyDoc(pending.before, "delete");
		}
	};

	/**
	 * Takes a row from the context selector without taking the selector off
	 * screen. The pick consumes the `@handle`, which would otherwise close it, so
	 * all of this is batched: the selector must never see itself closed, or it
	 * would reopen on its first tab rather than the one the user is on. The
	 * accounts listed and the highlight are put back as they were, so the list
	 * the user is walking doesn't reload under them.
	 */
	const pickFromContext = (pick: () => void) => {
		const index = highlightedIndex();
		const listed = completions();
		batch(() => {
			pick();
			setPickedFromContext(true);
			setCompletions(listed);
			setHighlightedIndex(index);
		});
	};

	/**
	 * The context selector, while it is on screen for an `@`-mention. The caret
	 * never leaves the question, so the keys that drive the selector arrive here.
	 */
	let contextNavigator: QuestionContextNavigator | null = null;

	const registerContextNavigator = (
		navigator: QuestionContextNavigator | null,
	) => {
		contextNavigator = navigator;
	};

	/**
	 * Walks the selector with the arrow keys: down and up the rows of the tab on
	 * screen, left and right between tabs, Enter to take the highlighted row. Tab
	 * moves focus within the input / add-button / popover trap instead. Returns
	 * false for anything else, and for a key carrying a modifier, which keeps its
	 * editing meaning (extend the selection, break the line).
	 */
	const handleContextSelectorKey = (e: KeyboardEvent): boolean => {
		if (!contextNavigator || !contextSelectorOpen()) return false;
		return handleContextNavigatorKey(
			contextNavigator,
			highlightedIndex(),
			e,
			setHighlightedIndex,
		);
	};

	const handleKeyDown = async (e: KeyboardEvent) => {
		// Keep the model's caret in step with the live DOM before any model reads.
		syncModelFromDom();

		// Take over undo/redo entirely: native contenteditable history can't see
		// our programmatic pill/mention edits (see the history manager above).
		if ((e.metaKey || e.ctrlKey) && !e.altKey) {
			const key = e.key.toLowerCase();
			if (key === "z" && !e.shiftKey) {
				e.preventDefault();
				undo();
				return;
			}
			if ((key === "z" && e.shiftKey) || key === "y") {
				e.preventDefault();
				redo();
				return;
			}
		}

		// Option-Command-C: the debug copy. Matched on `code`, since Option rewrites
		// `key` to the character it would compose (Option-C is "ç").
		if ((e.metaKey || e.ctrlKey) && e.altKey && e.code === "KeyC") {
			e.preventDefault();
			copyAugmentedPrompt();
			return;
		}

		const mentionUnderCaret =
			mentionsEnabled() &&
			mentionAtCaret(doc, { phrase: contextSelectorOpen() }) != null;
		// Space and punctuation commit or end a mention. While the selector is
		// open they are more of the search: a title is a phrase, and typing
		// one must not take the selector away.
		if (
			e.key.length === 1 &&
			MENTION_DELIMITER.test(e.key) &&
			mentionUnderCaret &&
			!contextSelectorOpen()
		) {
			e.preventDefault();
			const handled = await tryResolveMentionOnDelimiter(e.key, completions());
			if (handled) return;
		}

		// Escape abandons the mention being written, so the selector goes away even
		// before any account has matched what was typed.
		if (e.key === "Escape" && contextSelectorOpen()) {
			e.preventDefault();
			dismissContextMention();
			return;
		}

		// After a pick, typing goes back to the question: the selector goes, and
		// the key is left to land in the text as it would have anyway.
		if (pickedFromContext() && e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
			dismissCompletions();
		}

		// Ahead of the caret keys below: while the selector is open for a mention,
		// the arrows belong to it rather than to the document.
		if (handleContextSelectorKey(e)) {
			e.preventDefault();
			return;
		}

		// Keep the zero-width caret anchors around atoms invisible to the user:
		// arrows skip over them, and Backspace/Delete removes the whole
		// atom+anchors unit instead of stranding a lone anchor.
		if (hasAtoms()) {
			if (
				(e.key === "ArrowLeft" || e.key === "ArrowRight") &&
				!e.altKey &&
				!e.metaKey &&
				!e.ctrlKey
			) {
				const dir = e.key === "ArrowLeft" ? "left" : "right";
				const handled = e.shiftKey
					? extendSelectionAcrossAtom(dir)
					: moveCaretAcrossAtom(dir);
				if (handled) {
					e.preventDefault();
					return;
				}
			}

			// Word-wise moves: Option-Arrow on a Mac, Ctrl-Arrow elsewhere. Command
			// is excluded — that's line start/end, which stays native.
			if (
				(e.key === "ArrowLeft" || e.key === "ArrowRight") &&
				(e.altKey || e.ctrlKey) &&
				!e.metaKey
			) {
				const dir = e.key === "ArrowLeft" ? "left" : "right";
				if (moveCaretByWord(dir, e.shiftKey)) {
					e.preventDefault();
					return;
				}
			}

			if (e.key === "Backspace" || e.key === "Delete") {
				const range = collapsedInputRange();
				const atom = range
					? adjacentAtom(range, e.key === "Backspace" ? "left" : "right")
					: null;
				if (atom) {
					e.preventDefault();
					removeAtomFromModel(atom);
					return;
				}
			}
		}

		if (e.key === "Enter" && e.shiftKey && !e.ctrlKey && !e.altKey) {
			// Insert the newline ourselves (deterministic, list-aware) rather than
			// letting the browser choose a <div>/<p> wrapper.
			e.preventDefault();
			insertLineBreak();
			return;
		}

		if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.altKey) {
			e.preventDefault();
			submit();
		}
	};

	const handleClickOutside = (e: MouseEvent) => {
		if (
			completions().length > 0 &&
			wrapperRef &&
			!wrapperRef.contains(e.target as Node)
		) {
			dismissCompletions();
		}
	};

	const handleWrapperMouseUp = (e: MouseEvent) => {
		const target = e.target as HTMLElement;
		if (target.tagName === "DIV") {
			inputRef?.focus();
		}
		if (mentionsEnabled() && inputRef?.contains(target)) {
			// Selection moves after mousedown; sync once it has settled.
			queueMicrotask(() => {
				syncModelFromDom();
				handleMentionSearchModel();
			});
		}
	};

	onMount(() => {
		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("selectionchange", updateAtomSelectionState);
		document.addEventListener("selectionchange", updateActiveMarkdownToken);
		document.addEventListener("selectionchange", syncModelFromDom);

		if (props.onReady) {
			props.onReady({
				focus: () => inputRef?.focus(),
				blur: () => inputRef?.blur(),
				clear: clearInput,
				getText: () => toAugmentedText(doc).trim(),
				insertVideoMentions,
			});
		}

		if (props.autofocus && !isInputDisabled()) {
			inputRef?.focus();
			placeCaretAtEnd();
		}
	});

	onCleanup(() => {
		if (mentionDebounceTimer !== undefined) clearTimeout(mentionDebounceTimer);
		inputResizeObserver?.disconnect();
		document.removeEventListener("mousedown", handleClickOutside);
		document.removeEventListener("selectionchange", updateAtomSelectionState);
		document.removeEventListener("selectionchange", updateActiveMarkdownToken);
		document.removeEventListener("selectionchange", syncModelFromDom);
	});

	const variant = () => props.variant ?? "default";
	const isDisabled = () => props.disabled ?? false;
	const isInputDisabled = () => isDisabled() || (props.inputLocked ?? false);
	const placeholderText = () => props.placeholder ?? "Ask a question";
	const inputRowAlign = (): "center" | "end" =>
		isSingleLineInput() ? "center" : "end";

	return {
		assignInputRef,
		setWrapperRef: (el: HTMLDivElement | undefined) => {
			wrapperRef = el;
		},
		question,
		completions,
		mentionQuery,
		contextSelectorOpen,
		highlightedIndex,
		setHighlightedIndex,
		registerContextNavigator,
		selectedProjectId,
		selectedPoolId,
		setSelectedProjectId,
		setSelectedPoolId,
		acceptCompletion,
		handleInput,
		handleBeforeInput,
		handleCompositionStart,
		handleCompositionEnd,
		handleKeyDown,
		handlePaste,
		handleCopy,
		handleCut,
		submit,
		hideHelpText,
		variant,
		isDisabled,
		isInputDisabled,
		placeholderText,
		inputRowAlign,
		handleWrapperMouseUp,
		isProjectSelectorDisabled,
		beginSpeechSession,
		endSpeechSession,
		applySpeechTranscript,
		insertVideoMentions,
		insertedVideoMentionKeys,
		insertAccountMentions,
		insertedAccountMentionKeys,
		removeMentionAtCaret,
		beginContextMention,
		dismissContextMention,
		pickFromContext,
		searchContextVideos,
		browsesContextPool,
	};
}
